import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { UserType } from '../../constants/userTypes';
import { CURRENT_TOS_VERSION } from '../../config/tos';
import GlobalNavBar from '../layout/GlobalNavBar';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface LocationState {
  userId: string;
  email: string;
  emailSynthetic?: boolean; // Naver may return a placeholder email
  name: string;
  profileImage?: string;
  oauthProvider?: string;
  tokens?: { accessToken: string; idToken: string; refreshToken?: string };
  naverId?: string;
}

interface CompleteRegistrationFormData {
  name: string;
  email: string;
  userType: UserType;
  tosAccepted: boolean;
  marketingConsent: boolean;
}

/**
 * Registration completion page for new OAuth users.
 * Same layout as RegisterForm but without password fields,
 * pre-filled with data from the OAuth provider.
 */
export const CompleteOAuthRegistration: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const setAuthenticatedUser = useAuthStore((state) => state.setAuthenticatedUser);

  const state = location.state as LocationState | undefined;

  // When the OAuth provider (Naver) couldn't give us a real email, the
  // backend synthesizes `naver_<id>@naver.local`. We don't want the user to
  // complete registration with a non-routable email, so in that case we
  // clear the pre-fill and require them to type a real one.
  const emailSynthetic = state?.emailSynthetic === true;

  // Pre-fill form with OAuth provider data
  const {
    register: registerField,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CompleteRegistrationFormData>({
    defaultValues: {
      name: state?.name || '',
      email: emailSynthetic ? '' : state?.email || '',
      userType: '' as UserType,
      tosAccepted: false,
      marketingConsent: false,
    },
  });

  // ─── Email verification state ─────────────────────────────────────────
  // The backend's completeOAuthRegistration gate requires a verified email
  // record for Naver (and any other provider that might pass through
  // without a pre-verified email). We track send/confirm state locally and
  // block the final submit until the typed email has been verified. The
  // tracked `verifiedEmail` string is compared to the current form value
  // so editing the email after verification re-locks the form.
  const watchedEmail = watch('email');
  const [sendingCode, setSendingCode] = React.useState(false);
  const [codeSent, setCodeSent] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [verifying, setVerifying] = React.useState(false);
  const [verifiedEmail, setVerifiedEmail] = React.useState<string>('');
  const needsEmailVerification = emailSynthetic; // applies to Naver users today
  const isEmailVerified =
    !!verifiedEmail &&
    !!watchedEmail &&
    verifiedEmail.trim().toLowerCase() === watchedEmail.trim().toLowerCase();

  const handleSendCode = async () => {
    const email = (watchedEmail || '').trim();
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      toast.error(t('auth:errors.invalidEmail'));
      return;
    }
    setSendingCode(true);
    try {
      const res = await graphqlApi.sendEmailVerification(email);
      if (res.success) {
        setCodeSent(true);
        setVerifiedEmail('');
        toast.success(t('auth:codeSent'));
      } else {
        toast.error(res.error || t('auth:errors.forgotPasswordFailed'));
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    const email = (watchedEmail || '').trim();
    if (!email || !verificationCode) return;
    setVerifying(true);
    try {
      const res = await graphqlApi.confirmEmailVerification(email, verificationCode);
      if (res.success) {
        setVerifiedEmail(email);
        toast.success(t('auth:success.emailConfirmed'));
      } else {
        toast.error(res.error || t('auth:authErrors.CodeMismatchException'));
      }
    } finally {
      setVerifying(false);
    }
  };

  // If no OAuth state was passed, redirect to login
  if (!state?.userId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-gray-600 mb-4">
            {t('auth:sessionExpired', 'Session expired. Please try again.')}
          </p>
          <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
            {t('auth:backToLogin', 'Back to Login')}
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: CompleteRegistrationFormData) => {
    // When email verification is required, make sure the user verified the
    // exact email they're submitting. Backend also enforces this but we
    // block here so the user doesn't see a last-minute error toast.
    if (needsEmailVerification && !isEmailVerified) {
      toast.error(
        t('auth:emailVerificationRequired', {
          defaultValue: '이메일 인증을 먼저 완료해주세요.',
        }),
      );
      return;
    }
    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      const result = await graphqlApi.completeOAuthRegistration({
        userId: state.userId,
        email: data.email,
        name: data.name,
        userType: data.userType,
        profileImage: state.profileImage || null,
        oauthProvider: state.oauthProvider,
        tosAcceptedVersion: CURRENT_TOS_VERSION,
        tosAcceptedAt: now,
        marketingConsent: data.marketingConsent,
        ...(state.naverId && { naverId: state.naverId }),
      });

      if (!result.success) {
        // Localized toast for the common "email already in use" case —
        // especially relevant to Naver OAuth users who typed a real email
        // in place of the `@naver.local` placeholder and accidentally
        // collided with an existing native account.
        if ((result as any).errorCode === 'EmailAlreadyInUse') {
          toast.error(
            t('auth:authErrors.UsernameExistsException', {
              defaultValue: '이미 존재하는 이메일입니다.',
            }),
          );
          setIsLoading(false);
          return;
        }
        if ((result as any).errorCode === 'EmailNotVerified') {
          toast.error(
            t('auth:emailVerificationRequired', {
              defaultValue: '이메일 인증을 먼저 완료해주세요.',
            }),
          );
          setIsLoading(false);
          return;
        }
        throw new Error(result.error || 'Failed to complete registration');
      }

      setAuthenticatedUser({
        userId: state.userId,
        email: data.email,
        name: data.name,
        userType: data.userType,
        createdAt: now,
        updatedAt: now,
      });

      // Promote the Cognito tokens (held back by handleNaverCallback so
      // the user wasn't treated as signed in before finishing this form)
      // into Amplify's localStorage now that registration is complete.
      if (state.tokens?.idToken && state.tokens?.accessToken) {
        try {
          const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';
          const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
          let lastAuthUser = state.userId;
          try {
            const payload = JSON.parse(atob(state.tokens.idToken.split('.')[1]));
            lastAuthUser = payload.sub || payload['cognito:username'] || state.userId;
          } catch { /* keep fallback */ }
          localStorage.setItem(`${keyPrefix}.LastAuthUser`, lastAuthUser);
          localStorage.setItem(`${keyPrefix}.${lastAuthUser}.idToken`, state.tokens.idToken);
          localStorage.setItem(`${keyPrefix}.${lastAuthUser}.accessToken`, state.tokens.accessToken);
          if ((state.tokens as any).refreshToken) {
            localStorage.setItem(`${keyPrefix}.${lastAuthUser}.refreshToken`, (state.tokens as any).refreshToken);
          }
          localStorage.setItem(`${keyPrefix}.${lastAuthUser}.clockDrift`, '0');
          useAuthStore.setState({ token: state.tokens.idToken });
        } catch (err) {
          console.warn('⚠️ Failed to promote OAuth tokens to Amplify storage:', err);
        }
      }

      toast.success(t('auth:registrationComplete', 'Registration complete!'));
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('❌ Complete registration error:', error);
      toast.error(error.message || t('auth:failedToCompleteRegistration', 'Failed to complete registration'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />

      {/* Content */}
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:completeRegistration', 'Complete Your Registration')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth:completeRegistrationDesc', 'Welcome! Please fill in the remaining details to continue.')}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Name - pre-filled from OAuth */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('auth:fullName')}
              </label>
              <input
                {...registerField('name', {
                  required: t('auth:errors.nameRequired'),
                  minLength: {
                    value: 2,
                    message: t('auth:errors.nameMinLength'),
                  },
                })}
                type="text"
                className="mt-1 input"
                placeholder={t('auth:enterFullName')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Email - pre-filled from OAuth provider. Read-only when the
                provider gave us a real email; editable + required when the
                Naver backend returned a synthetic `@naver.local` placeholder
                (Naver users can decline email consent entirely, so we ask
                them to supply a real one here). */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('auth:emailAddress')}
              </label>
              <input
                {...registerField('email', {
                  required: t('auth:errors.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth:errors.invalidEmail'),
                  },
                })}
                type="email"
                autoComplete="email"
                className={`mt-1 input ${emailSynthetic ? '' : 'bg-gray-50'}`}
                placeholder={emailSynthetic ? t('auth:enterEmail') : undefined}
                readOnly={!emailSynthetic}
              />
              {emailSynthetic && (
                <p className="mt-1 text-xs text-gray-500">
                  {t('auth:naverEmailRequired', {
                    defaultValue:
                      '네이버에서 이메일 정보를 제공받지 못했습니다. 사용하실 이메일을 입력해주세요.',
                  })}
                </p>
              )}
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}

              {/* Email verification controls — only for OAuth flows that
                  require the user to verify the email they typed (Naver). */}
              {needsEmailVerification && (
                <div className="mt-3 space-y-2">
                  {!isEmailVerified ? (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d{6}"
                          maxLength={6}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder={t('auth:enter6DigitCode')}
                          disabled={!codeSent}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50"
                        />
                        <button
                          type="button"
                          onClick={handleSendCode}
                          disabled={sendingCode || !watchedEmail}
                          className="px-3 py-2 text-xs font-medium rounded-md border border-primary-600 text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {sendingCode
                            ? t('auth:sending')
                            : codeSent
                              ? t('auth:resendCode')
                              : t('auth:sendEmailVerificationCode', { defaultValue: '인증코드 발송' })}
                        </button>
                        <button
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={verifying || !codeSent || verificationCode.length !== 6}
                          className="px-3 py-2 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {verifying
                            ? t('auth:confirming')
                            : t('auth:confirmEmail', { defaultValue: '확인' })}
                        </button>
                      </div>
                      {codeSent && (
                        <p className="text-[11px] text-gray-500">
                          {t('auth:codeSent')}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs font-medium text-green-600 flex items-center gap-1">
                      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('auth:success.emailConfirmed')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Account Type */}
            <div>
              <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                {t('auth:accountType')}
              </label>
              <select
                {...registerField('userType', {
                  required: t('auth:errors.accountTypeRequired'),
                })}
                className="mt-1 input"
              >
                <option value="">{t('auth:selectAccountType')}</option>
                <option value="brand">{t('auth:brand')}</option>
                <option value="agency">{t('auth:agency')}</option>
                <option value="influencer">{t('auth:influencerCreator')}</option>
              </select>
              {errors.userType && (
                <p className="mt-1 text-sm text-red-600">{errors.userType.message}</p>
              )}
            </div>

            {/* Terms of Service Acceptance */}
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  {...registerField('tosAccepted', {
                    required: t('auth:errors.tosRequired'),
                  })}
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="tosAccepted" className="ml-2 block text-sm text-gray-700">
                  {t('auth:tosAcceptance')}{' '}
                  <Link
                    to="/legal/terms-of-service"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 underline"
                  >
                    {t('auth:termsOfService')}
                  </Link>
                  {' '}{t('auth:and')}{' '}
                  <Link
                    to="/legal/privacy-policy"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 underline"
                  >
                    {t('auth:privacyPolicy')}
                  </Link>
                </label>
              </div>
              {errors.tosAccepted && (
                <p className="ml-6 text-sm text-red-600">{errors.tosAccepted.message}</p>
              )}

              {/* Marketing Consent (Optional) */}
              <div className="flex items-start">
                <input
                  {...registerField('marketingConsent')}
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="marketingConsent" className="ml-2 block text-sm text-gray-700">
                  {t('auth:marketingConsent')}
                </label>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || (needsEmailVerification && !isEmailVerified)}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? t('auth:completing', 'Completing...')
                : t('auth:completeRegistrationBtn', 'Complete Registration')}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};
